(() => {
  'use strict'

  const STORAGE_KEY = 'lcs_attribution_v1'
  const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000
  const MAX_VALUE_LENGTH = 500
  const CLICK_IDS = ['gclid', 'gbraid', 'wbraid']
  const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
  const SIGNAL_FIELDS = [...CLICK_IDS, ...UTM_FIELDS]
  const CHECKOUT_SYNC_TIMEOUT_MS = 1200
  let pendingTouch = currentTouch()
  let checkoutReleased = false
  let syncInFlight = null

  document.documentElement.dataset.lcsAttribution = 'loading'

  function clean(value, maxLength = MAX_VALUE_LENGTH) {
    return typeof value === 'string' ? value.trim().slice(0, maxLength) : ''
  }

  function safePath(urlValue) {
    try {
      const url = new URL(urlValue, location.origin)
      return url.origin === location.origin ? clean(url.pathname, 500) : ''
    } catch (_) {
      return ''
    }
  }

  function safeReferrerHost() {
    try {
      if (!document.referrer) return ''
      const url = new URL(document.referrer)
      return url.origin === location.origin ? '' : clean(url.hostname.toLowerCase(), 255)
    } catch (_) {
      return ''
    }
  }

  function randomId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `lcs-${window.crypto.randomUUID()}`
    }
    return `lcs-${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  function readStored() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
      if (!stored || !stored.captured_at || Date.now() - Date.parse(stored.captured_at) > MAX_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY)
        return null
      }
      return stored
    } catch (_) {
      return null
    }
  }

  function writeStored(value) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
      return true
    } catch (_) {
      return false
    }
  }

  function currentTouch() {
    const query = new URLSearchParams(location.search)
    const touch = {
      landing_path: safePath(location.href),
      referrer_host: safeReferrerHost()
    }

    // Google can add more than one supported click-ID type to the same landing
    // URL. Keep every supplied ID so the server-side event can send the complete
    // adIdentifiers object instead of silently discarding an attributable visit.
    for (const name of CLICK_IDS) {
      const value = clean(query.get(name))
      if (value) touch[name] = value
    }
    for (const name of UTM_FIELDS) {
      const value = clean(query.get(name))
      if (value) touch[name] = value
    }
    return touch
  }

  function hasCampaignSignal(touch) {
    return SIGNAL_FIELDS.some((name) => Boolean(touch[name]))
  }

  function capture(touchOverride) {
    const now = new Date().toISOString()
    const touch = touchOverride || currentTouch()
    const stored = readStored()

    if (!stored) {
      const created = {
        attribution_id: randomId(),
        captured_at: now,
        updated_at: now,
        first: touch,
        last: touch
      }
      writeStored(created)
      return created
    }

    // Keep first touch immutable. Direct repeat visits do not overwrite the
    // most recent attributable campaign.
    if (hasCampaignSignal(touch)) {
      stored.last = touch
      stored.updated_at = now
      writeStored(stored)
    }
    return stored
  }

  function privateCartAttributes(attribution) {
    const first = attribution.first || {}
    const last = attribution.last || {}
    const attributes = {
      __lcs_attr_id: clean(attribution.attribution_id, 100),
      __lcs_attr_captured: clean(attribution.captured_at, 40),
      __lcs_marketing_consent: 'granted',
      __lcs_marketing_consent_at: new Date().toISOString(),
      __lcs_first_landing: clean(first.landing_path),
      __lcs_first_referrer: clean(first.referrer_host, 255),
      __lcs_last_landing: clean(last.landing_path),
      __lcs_last_referrer: clean(last.referrer_host, 255)
    }
    for (const name of SIGNAL_FIELDS) {
      if (first[name]) attributes[`__lcs_first_${name}`] = clean(first[name])
      if (last[name]) attributes[`__lcs_last_${name}`] = clean(last[name])
    }
    return attributes
  }

  async function performCartSync(consentConfirmedByEvent = false) {
    if (!consentConfirmedByEvent && !marketingAllowed()) return false
    const attribution = capture(pendingTouch)
    try {
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/'
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: privateCartAttributes(attribution) })
      })
      if (!response.ok) throw new Error(`cart update ${response.status}`)
      pendingTouch = null
      document.documentElement.dataset.lcsAttribution = 'synced'
      return true
    } catch (_) {
      document.documentElement.dataset.lcsAttribution = 'sync-failed'
      return false
    }
  }

  function syncToCart(consentConfirmedByEvent = false) {
    if (syncInFlight) return syncInFlight
    syncInFlight = performCartSync(consentConfirmedByEvent).finally(() => {
      syncInFlight = null
    })
    return syncInFlight
  }

  function marketingAllowed() {
    try {
      return Boolean(
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        window.Shopify.customerPrivacy.marketingAllowed()
      )
    } catch (_) {
      return false
    }
  }

  function start() {
    if (!marketingAllowed()) {
      document.documentElement.dataset.lcsAttribution = 'consent-required'
      return
    }
    void syncToCart()
  }

  async function clearPersistedAttribution() {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (_) {
      // Storage can be unavailable; cart cleanup still proceeds.
    }
    pendingTouch = null
    const attributes = {
      __lcs_attr_id: null,
      __lcs_attr_captured: null,
      __lcs_marketing_consent: null,
      __lcs_marketing_consent_at: null,
      __lcs_first_landing: null,
      __lcs_first_referrer: null,
      __lcs_last_landing: null,
      __lcs_last_referrer: null
    }
    for (const name of SIGNAL_FIELDS) {
      attributes[`__lcs_first_${name}`] = null
      attributes[`__lcs_last_${name}`] = null
    }
    try {
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/'
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes })
      })
    } finally {
      document.documentElement.dataset.lcsAttribution = 'consent-required'
    }
  }

  function handleConsentChoice(event) {
    const eventDecision = event && event.detail && event.detail.marketingAllowed
    if (eventDecision === true) {
      // Shopify documents visitorConsentCollected.detail as the authoritative
      // updated permission state. Use it directly so an API-state refresh race
      // cannot drop the landing click that preceded the visitor's choice.
      void syncToCart(true)
    } else if (eventDecision === false) {
      void clearPersistedAttribution()
    } else if (marketingAllowed()) {
      void syncToCart()
    } else {
      void clearPersistedAttribution()
    }
  }

  function syncWithTimeout() {
    return Promise.race([
      syncToCart(),
      new Promise((resolve) => window.setTimeout(() => resolve(false), CHECKOUT_SYNC_TIMEOUT_MS))
    ])
  }

  async function releaseCheckout(action) {
    await syncWithTimeout()
    checkoutReleased = true
    action()
  }

  document.addEventListener('visitorConsentCollected', handleConsentChoice)
  document.addEventListener('click', (event) => {
    if (checkoutReleased || !marketingAllowed()) return
    const checkoutLink = event.target && event.target.closest
      ? event.target.closest('a[href*="/checkout"]')
      : null
    if (!checkoutLink) return
    event.preventDefault()
    void releaseCheckout(() => location.assign(checkoutLink.href))
  }, true)

  document.addEventListener('submit', (event) => {
    if (checkoutReleased || !marketingAllowed()) return
    const form = event.target
    const submitter = event.submitter || document.activeElement
    const isCheckout = form && form.matches && (
      (submitter && submitter.matches && submitter.matches('[name="checkout"]')) ||
      (form.action && form.action.includes('/checkout'))
    )
    if (!isCheckout) return
    event.preventDefault()
    void releaseCheckout(() => {
      const validSubmitter = submitter && submitter.form === form ? submitter : null
      if (typeof form.requestSubmit === 'function') {
        if (validSubmitter) form.requestSubmit(validSubmitter)
        else form.requestSubmit()
      }
      else form.submit()
    })
  }, true)

  for (const eventName of ['cart:updated', 'cart:refresh', 'product:added']) {
    document.addEventListener(eventName, () => void syncToCart())
  }
  window.addEventListener('pageshow', () => void syncToCart())

  window.LCSAttribution = Object.freeze({ sync: syncToCart })

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
    document.documentElement.dataset.lcsAttribution = 'privacy-api-unavailable'
    return
  }

  window.Shopify.loadFeatures(
    [{ name: 'consent-tracking-api', version: '0.1' }],
    (error) => {
      if (error) {
        document.documentElement.dataset.lcsAttribution = 'privacy-api-error'
        return
      }
      start()
    }
  )
})()
